import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { LanguageToggle } from '@/components/LanguageToggle';
import { toast } from 'sonner';
import { FilmSlate, Plus, SignOut, Clock, FilmStrip, Trash, MagnifyingGlass, Question } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newProject, setNewProject] = useState({
    title: '',
    description: '',
    style: 'cinematic',
    pacing: 'balanced',
    aspect_ratio: '16:9'
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      setProjects(res.data);
    } catch (err) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.title.trim()) {
      toast.error('Please enter a project title');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/projects`, newProject);
      setProjects([res.data, ...projects]);
      setShowCreateModal(false);
      setNewProject({ title: '', description: '', style: 'cinematic', pacing: 'balanced', aspect_ratio: '16:9' });
      toast.success(t('projectCreated'));
      navigate(`/project/${res.data.id}`);
    } catch (err) {
      toast.error(t('errorOccurred'));
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteProject = async (e, projectId) => {
    e.stopPropagation();
    if (!window.confirm(language === 'es' ? '¿Estás seguro de que quieres eliminar este proyecto?' : 'Are you sure you want to delete this project?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      setProjects(projects.filter(p => p.id !== projectId));
      toast.success(t('projectDeleted'));
    } catch (err) {
      toast.error(t('errorOccurred'));
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const filteredProjects = projects.filter(p => 
    p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="bg-[#171717] border-b border-[#333333]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FF5D01] rounded-lg flex items-center justify-center">
              <FilmSlate size={24} weight="fill" className="text-white" />
            </div>
            <span className="text-2xl font-bold text-white tracking-tight">Script Visual</span>
          </div>
          <div className="flex items-center gap-4">
            <LanguageToggle />
            <a
              href="/guide.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#A1A1AA] hover:text-white transition-colors p-1"
              title={t('userGuide')}
              data-testid="user-guide-link"
            >
              <Question size={22} weight="bold" />
            </a>
            <span className="text-[#A1A1AA] text-sm">{user?.name}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-[#A1A1AA] hover:text-white hover:bg-[#262626]"
              data-testid="logout-btn"
              title={t('logout')}
            >
              <SignOut size={20} />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-1">{language === 'es' ? 'Tus Proyectos' : 'Your Projects'}</h1>
            <p className="text-[#A1A1AA]">{projects.length} {projects.length !== 1 ? t('scenes') : t('scene')}</p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-[#FF5D01] hover:bg-[#e65400] text-white gap-2"
            data-testid="create-project-btn"
          >
            <Plus size={20} weight="bold" />
            {t('newProject')}
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
          <Input
            placeholder={t('searchProjects')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-[#171717] border-[#333333] text-white pl-10 placeholder:text-[#A1A1AA] focus:ring-[#FF5D01] focus:border-[#FF5D01]"
            data-testid="search-projects-input"
          />
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#FF5D01] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-[#171717] rounded-full flex items-center justify-center mx-auto mb-4">
              <FilmStrip size={32} className="text-[#A1A1AA]" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">
              {searchQuery ? (language === 'es' ? 'No se encontraron proyectos' : 'No projects found') : t('noProjects')}
            </h3>
            <p className="text-[#A1A1AA] mb-6">
              {searchQuery ? (language === 'es' ? 'Intenta con otro término de búsqueda' : 'Try a different search term') : t('createFirst')}
            </p>
            {!searchQuery && (
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-[#FF5D01] hover:bg-[#e65400] text-white gap-2"
                data-testid="create-first-project-btn"
              >
                <Plus size={20} weight="bold" />
                {t('createProject')}
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project, index) => (
              <div
                key={project.id}
                onClick={() => navigate(`/project/${project.id}`)}
                className="bg-[#171717] border border-[#333333] rounded-lg overflow-hidden cursor-pointer hover:border-[#FF5D01] transition-colors group animate-slide-up"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`project-card-${project.id}`}
              >
                <div className="h-32 bg-[#262626] flex items-center justify-center relative">
                  <FilmSlate size={48} className="text-[#333333]" />
                  <button
                    onClick={(e) => handleDeleteProject(e, project.id)}
                    className="absolute top-2 right-2 p-2 bg-[#171717] rounded-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#EF4444] text-[#A1A1AA] hover:text-white"
                    data-testid={`delete-project-${project.id}`}
                  >
                    <Trash size={16} />
                  </button>
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-white mb-1 truncate">{project.title}</h3>
                  <p className="text-[#A1A1AA] text-sm mb-3 line-clamp-2">
                    {project.description || (language === 'es' ? 'Sin descripción' : 'No description')}
                  </p>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-[#A1A1AA]">
                      <FilmStrip size={16} />
                      <span>{project.scene_count || 0} {t('scenes')}</span>
                    </div>
                    <div className="flex items-center gap-1 text-[#A1A1AA]">
                      <Clock size={16} />
                      <span>{formatDuration(project.total_duration || 0)}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <span className="px-2 py-0.5 bg-[#262626] rounded text-xs text-[#A1A1AA] capitalize">{project.style}</span>
                    <span className="px-2 py-0.5 bg-[#262626] rounded text-xs text-[#A1A1AA] capitalize">{project.pacing}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create Project Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[#171717] border-[#333333] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{t('createNewProject')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-[#F8FAFC]">{t('projectTitle')}</Label>
              <Input
                placeholder={t('projectTitlePlaceholder')}
                value={newProject.title}
                onChange={(e) => setNewProject({ ...newProject, title: e.target.value })}
                className="bg-[#0a0a0a] border-[#333333] text-white placeholder:text-[#A1A1AA]"
                data-testid="new-project-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#F8FAFC]">{t('description')} ({language === 'es' ? 'opcional' : 'optional'})</Label>
              <Textarea
                placeholder={t('descriptionPlaceholder')}
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                className="bg-[#0a0a0a] border-[#333333] text-white placeholder:text-[#A1A1AA] resize-none"
                rows={3}
                data-testid="new-project-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#F8FAFC]">{t('style')}</Label>
                <Select value={newProject.style} onValueChange={(v) => setNewProject({ ...newProject, style: v })}>
                  <SelectTrigger className="bg-[#0a0a0a] border-[#333333] text-white" data-testid="new-project-style-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#171717] border-[#333333]">
                    <SelectItem value="cinematic">{t('cinematic')}</SelectItem>
                    <SelectItem value="documentary">{t('documentary')}</SelectItem>
                    <SelectItem value="realistic">{t('realistic')}</SelectItem>
                    <SelectItem value="vintage">{language === 'es' ? 'Vintage' : 'Vintage'}</SelectItem>
                    <SelectItem value="dramatic">{t('dramatic')}</SelectItem>
                    <SelectItem value="modern">{language === 'es' ? 'YouTube Moderno' : 'Modern YouTube'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#F8FAFC]">{t('pacing')}</Label>
                <Select value={newProject.pacing} onValueChange={(v) => setNewProject({ ...newProject, pacing: v })}>
                  <SelectTrigger className="bg-[#0a0a0a] border-[#333333] text-white" data-testid="new-project-pacing-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#171717] border-[#333333]">
                    <SelectItem value="slower">{language === 'es' ? 'Cinematográfico Lento' : 'Slower Cinematic'}</SelectItem>
                    <SelectItem value="balanced">{language === 'es' ? 'Documental Equilibrado' : 'Balanced Documentary'}</SelectItem>
                    <SelectItem value="faster">{language === 'es' ? 'YouTube Rápido' : 'Faster YouTube'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowCreateModal(false)}
              className="text-[#A1A1AA] hover:text-white hover:bg-[#262626]"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleCreateProject}
              disabled={creating}
              className="bg-[#FF5D01] hover:bg-[#e65400] text-white"
              data-testid="confirm-create-project-btn"
            >
              {creating ? t('creating') : t('createProject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
